import { LuIcon, Text } from "@overmap-ai/blocks"
import { type AssetTypeStatus, EMPTY_ARRAY, type Stored, toUuidArray } from "@overmap-ai/common"
import { use, useCallback, useMemo } from "react"

import { AssetTypeStatusMultiSelect } from "../../../components/DropdownMultiSelects/AssetTypeStatusMultiSelect"
import { AssetTypeStatusSelect } from "../../../components/DropdownSelects/AssetTypeStatusSelect"
import { AssetTypeStatusIcon, AssetTypeStatusIcons } from "../../../components/Icons/AssetTypeStatusIcon"
import { modelIconMapping } from "../../../mappings/icons"
import { FilterModifierDropdown, RemoveFilterButton } from "../../components"
import { FilterCellBadge } from "../../components/FilterCellBadge"
import { FilterCellButton } from "../../components/FilterCellButton"
import { FilterCellRoot } from "../../components/FilterCellRoot"
import { FilterRendererContext } from "../../contexts/FilterRendererContext/context"
import type { FilterDefRenderProps } from "../BaseFilterDef"
import type { AssetTypeStatusFilterDef } from "./AssetTypeStatusFilterDef"

type AssetTypeStatusFilterCellProps = FilterDefRenderProps<AssetTypeStatusFilterDef>

export const AssetTypeStatusFilterCell = (props: AssetTypeStatusFilterCellProps) => {
	const { filter, filterDef, filterManager } = props

	const { size } = use(FilterRendererContext)

	const assetTypeStatuses = filterDef.assetTypeStatuses

	const filterValue = filter.getFilterValue()
	const filterModifier = filter.getFilterModifier()

	const selectedAssetTypeStatus = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.equals.isFilterValueValid(filterValue)) return undefined
		return assetTypeStatuses.find(({ uuid }) => uuid === filterValue)
	}, [assetTypeStatuses, filterValue, filterDef.modifiers.equals])

	const selectedAssetTypeStatuses = useMemo(() => {
		if (filterValue === undefined || !filterDef.modifiers.includes.isFilterValueValid(filterValue)) return undefined
		const set = new Set(filterValue)
		return assetTypeStatuses.filter(({ uuid }) => set.has(uuid))
	}, [assetTypeStatuses, filterValue, filterDef.modifiers.includes])

	const handleValueChange = useCallback(
		(value?: Stored<AssetTypeStatus>) => {
			filter.setFilterValue(value?.uuid)
		},
		[filter],
	)

	const handleValuesChange = useCallback(
		(values: Stored<AssetTypeStatus>[]) => {
			filter.setFilterValue(values.length > 0 ? toUuidArray(values) : undefined)
		},
		[filter],
	)

	const getFilterValueUi = () => {
		switch (filterModifier) {
			case "equals":
			case "notEquals": {
				const equalsModifier = filterDef.modifiers.equals
				const equalsModifierFilterValue =
					filterValue !== undefined && equalsModifier.isFilterValueValid(filterValue)
						? filterValue
						: undefined

				return (
					<AssetTypeStatusSelect
						size={size}
						required={false}
						value={selectedAssetTypeStatus}
						onValueChange={handleValueChange}
						assetTypeStatuses={assetTypeStatuses}
					>
						<FilterCellButton className="truncate">
							{equalsModifierFilterValue !== undefined ? (
								<>
									<AssetTypeStatusIcon assetTypeStatusId={equalsModifierFilterValue} />
									<Text>{selectedAssetTypeStatus?.name}</Text>
								</>
							) : (
								"..."
							)}
						</FilterCellButton>
					</AssetTypeStatusSelect>
				)
			}
			case "includes":
			case "excludes": {
				const getLabelUi = () => {
					switch ((selectedAssetTypeStatuses ?? EMPTY_ARRAY).length) {
						case 0:
							return "..."
						case 1:
							return (
								<>
									<AssetTypeStatusIcon assetTypeStatusId={selectedAssetTypeStatuses![0].uuid} />
									<Text className="truncate">{selectedAssetTypeStatuses![0].name}</Text>
								</>
							)
						default:
							return (
								<>
									<AssetTypeStatusIcons
										assetTypeStatusIds={toUuidArray(selectedAssetTypeStatuses!)}
									/>
									{`${selectedAssetTypeStatuses!.length} statuses`}
								</>
							)
					}
				}

				return (
					<AssetTypeStatusMultiSelect
						size={size}
						values={selectedAssetTypeStatuses ?? EMPTY_ARRAY}
						onValuesChange={handleValuesChange}
						assetTypeStatuses={assetTypeStatuses}
					>
						<FilterCellButton className="truncate">
							<span className="flex items-center gap-2 truncate">{getLabelUi()}</span>
						</FilterCellButton>
					</AssetTypeStatusMultiSelect>
				)
			}
		}
	}

	return (
		<FilterCellRoot>
			<FilterCellBadge>
				<LuIcon icon={modelIconMapping.status} />
				Status
			</FilterCellBadge>

			<FilterModifierDropdown filterDef={filterDef} filter={filter}>
				<FilterCellButton>
					{filterDef.getFilterModifier(filterModifier).modifier.render(filterValue)}
				</FilterCellButton>
			</FilterModifierDropdown>

			{getFilterValueUi()}

			<RemoveFilterButton filter={filter} filterManager={filterManager} />
		</FilterCellRoot>
	)
}
